/**
 * CoverCard — feed grid item. The cover art is the hero; everything else
 * (title/author/genre, reactions, save) stays compact and mostly hidden
 * until hover, so the grid reads as a wall of covers, not text.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bookmark, Check, Share2 } from 'lucide-react';
import { cx } from '@/components/site/primitives';
import { toast } from '@/components/ui/sonner';
import CoverArt from './CoverArt';
import ReactionBar from './ReactionBar';

export default function CoverCard({ entry, onOpen }) {
  const { book, style, featured, reactionCounts, myReaction, onToggleReaction, isOnShelf, onSave } = entry;

  const handleShare = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(`${window.location.origin}/app/cover-club#${book.id}`).catch(() => {});
    toast('Cover link copied to clipboard');
  };

  const handleSave = (e) => {
    e.stopPropagation();
    if (!isOnShelf) onSave();
  };

  return (
    <motion.div
      layout
      initial={false}
      whileHover={{ y: -6 }}
      transition={{ type: 'spring', stiffness: 340, damping: 24 }}
      className="group relative overflow-hidden rounded-2xl border border-line bg-card shadow-soft transition-shadow duration-300 hover:shadow-lift"
      data-testid="cover-club-card"
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpen(book.id)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(book.id); } }}
        className="block w-full cursor-pointer text-left"
        aria-label={`Open ${book.title}`}
      >
        <div className={cx('relative w-full overflow-hidden', featured ? 'aspect-[3/4]' : 'aspect-[2/3]')}>
          <CoverArt book={book} style={style} className="h-full w-full transition-transform duration-500 ease-out group-hover:scale-[1.04]" />

          <span aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/0 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-hover:from-black/45" />

          {featured && (
            <span className="absolute left-2 top-2 rounded-full bg-white/90 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-coral shadow-sm">
              Featured
            </span>
          )}
          <span className={cx('absolute left-2 rounded-full bg-black/35 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm', featured ? 'top-8' : 'top-2')}>
            {book.genres?.[0]}
          </span>

          <motion.button
            type="button"
            onClick={handleSave}
            whileTap={{ scale: 0.8 }}
            aria-label={isOnShelf ? 'On your bookshelf' : 'Save to TBR'}
            data-testid="cover-card-save"
            className={cx(
              'absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full shadow-sm backdrop-blur-sm transition-colors',
              isOnShelf ? 'bg-coral text-white' : 'bg-black/35 text-white hover:bg-coral'
            )}
          >
            <AnimatePresence mode="wait" initial={false}>
              {isOnShelf ? (
                <motion.span key="check" initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.4, opacity: 0 }} transition={{ type: 'spring', stiffness: 500, damping: 20 }}>
                  <Check className="h-3.5 w-3.5" />
                </motion.span>
              ) : (
                <motion.span key="bookmark" initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.4, opacity: 0 }} transition={{ type: 'spring', stiffness: 500, damping: 20 }}>
                  <Bookmark className="h-3.5 w-3.5" />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          <div className="pointer-events-none absolute inset-x-2 bottom-2 flex translate-y-2 items-center justify-between gap-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
            <div className="pointer-events-auto rounded-full bg-black/45 p-1 backdrop-blur-md">
              <ReactionBar counts={reactionCounts} myReaction={myReaction} onToggle={onToggleReaction} size="sm" tone="dark" />
            </div>
            <button
              type="button"
              onClick={handleShare}
              aria-label="Share cover"
              className="pointer-events-auto grid h-7 w-7 shrink-0 place-items-center rounded-full bg-black/45 text-white/90 backdrop-blur-md hover:text-white"
            >
              <Share2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="p-2.5">
        <p className="truncate font-display text-sm font-semibold text-ink">{book.title}</p>
        <p className="truncate text-xs text-subtle">{book.author}</p>
      </div>
    </motion.div>
  );
}
